import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Spin } from 'antd';

import TagBadge from './TagBadge';
import { formatCount } from '../../utils/counts';
import { referenceHolderLabel } from '../../utils/itemTagSettings';
import { mergeTargets, tagRefusal } from '../../utils/tags';

// A card's own filters — its driven elements', its worklist's and its
// lookups' picker filters — and an element set's lookups' picker filters
// (keepr-api § Merging tags). Since the tags rulings (b67) the merge rewrites them where
// the person merging may edit the card or the set, and reports the rest
// (`reason: 'card'` / `'elementSet'`).
const CARD_HOLDERS = new Set(['drivenFilter', 'worklistFilter', 'lookupFilter']);
const SET_HOLDERS = new Set(['elementSetLookupFilter']);
const isCardFilter = entry => CARD_HOLDERS.has(entry?.holder) || SET_HOLDERS.has(entry?.holder);

/** "3 card filters", "1 element set filter", "2 card and element set filters". */
function cardFiltersWords(entries) {
	const sets = entries.filter(entry => SET_HOLDERS.has(entry?.holder)).length;
	if (!sets) return formatCount(entries.length, 'card filter', 'card filters');
	if (sets === entries.length) return formatCount(sets, 'element set filter', 'element set filters');
	return formatCount(entries.length, 'card or element set filter', 'card and element set filters');
}

/** Why a setting will not (or did not) follow the merge, in a few words. */
function notFollowingWhy(entry, intoName) {
	switch (entry?.reason) {
		case 'card':
			return 'on a card you can’t edit';
		case 'elementSet':
			return 'in an element set you can’t edit';
		case 'changed':
			return 'it changed while the merge ran';
		case 'too_long':
			return `naming “${intoName}” would make it too long`;
		case 'gone':
			return 'it was deleted meanwhile';
		default:
			return null;
	}
}

/** A holder's name, a link where the API gives one (a card's filters: the card; a set's: the set). */
function HolderName({ entry }) {
	const name = entry?.name || referenceHolderLabel(entry);
	return entry?.link ? <a href={entry.link}>{name}</a> : <span>{name}</span>;
}

/** One line per setting that won't follow: its name (a link), its kind, why. */
function NotFollowingList({ entries, intoName }) {
	return (
		<ul className="tag-merge__holders">
			{entries.map(entry => {
				const why = notFollowingWhy(entry, intoName);
				return (
					<li key={`${entry.holder}:${entry.id}`} data-testid="tag-merge-not-following">
						<HolderName entry={entry} />
						{` — ${referenceHolderLabel(entry)}${why ? `, ${why}` : ''}`}
					</li>
				);
			})}
		</ul>
	);
}

/**
 * MERGE INTO… (docs/plans/tags.md §5.2 Settings › Tags "merge (T9)"; keepr-api
 * docs/SCHEMA.md § Merging tags). Two steps in one dialog:
 *
 * 1. **Pick** the tag it goes into — every other tag of the same owner, indented
 *    like the tree, each one it can't go into listed disabled with why
 *    (restricted, automatic, beneath it: utils/tags.js canMergeInto).
 * 2. **Confirm**, with the counts a dry run returned (the API asks every
 *    question and writes nothing): how many items it moves to, the tags under
 *    it, the names kept as aliases (and the ones that don't fit), the filters
 *    and rules that will name the other tag — a card's own filters (its driven
 *    elements', its worklist's) among them where the person may edit the card
 *    (b67), each such card named — and the ones that won't follow, each named
 *    with why (a card they can't edit). A merge can't be undone, so the button
 *    is the destructive one, and it says so.
 *
 * `service.merge(id, into, { dryRun })` → `{ tag, merge }`. `onMerged({ tag,
 * merge })` hands back the survivor and the summary once it lands. A refusal —
 * from the dry run or the merge — is shown here and the dialog stays open.
 * A merge that lands with settings left behind (the answer's `notRewritten`:
 * a card's filter someone edited meanwhile, one that would grow too long,
 * one deleted — or a card the person can't edit) keeps the dialog open on a
 * last step that lists them, each a link, with **Done** (⚑); otherwise the
 * dialog closes, as a save does.
 */
export default function TagMergeModal({ open, onClose, tag, tags = [], service, onMerged, privateTags = false }) {
	const [choice, setChoice] = useState(null);
	const [plan, setPlan] = useState(null); // the dry run's `merge`
	const [checking, setChecking] = useState(false);
	const [busy, setBusy] = useState(false);
	const [refusal, setRefusal] = useState(null);
	const [result, setResult] = useState(null); // the answer's `merge`, when settings stayed behind
	const targets = useMemo(() => (tag ? mergeTargets(tags, tag) : []), [tags, tag]);
	const chosen = targets.find(target => target.id === choice) || null;

	useEffect(() => {
		if (!open) return;
		setChoice(null);
		setPlan(null);
		setChecking(false);
		setBusy(false);
		setRefusal(null);
		setResult(null);
	}, [open]);

	const check = async () => {
		if (!choice) return;
		setChecking(true);
		setRefusal(null);
		try {
			const { merge } = await service.merge(tag._id, choice, { dryRun: true });
			setPlan(merge || {});
		} catch (error) {
			setRefusal(tagRefusal(error, 'Could not check this merge. Please try again.'));
		} finally {
			setChecking(false);
		}
	};

	const submit = async () => {
		setBusy(true);
		setRefusal(null);
		try {
			const answer = await service.merge(tag._id, choice);
			if (typeof onMerged === 'function') onMerged(answer);
			const left = answer?.merge?.queries?.notRewritten || [];
			if (left.length) setResult(answer.merge);
			else onClose();
		} catch (error) {
			setRefusal(tagRefusal(error, 'The tags were not merged. Please try again.'));
		} finally {
			setBusy(false);
		}
	};

	if (!tag) return null;
	const into = chosen?.tag || plan?.into || null;
	const intoName = into?.name || '';

	const summary = () => {
		const lines = [];
		const items = plan.private ? (plan.private.items || 0) + (plan.private.collections || 0) : plan.items;
		lines.push(items
			? `“${intoName}” goes on the ${formatCount(items, plan.private ? 'item or collection' : 'item', plan.private ? 'items and collections' : 'items')} “${tag.name}” is on.`
			: `“${tag.name}” is on nothing yet.`);
		if (plan.children) lines.push(`The ${formatCount(plan.children, 'tag', 'tags')} under it ${plan.children === 1 ? 'moves' : 'move'} under “${intoName}”.`);
		const added = plan.aliases?.added || [];
		if (added.length) lines.push(`“${intoName}” also answers to ${added.map(name => `“${name}”`).join(', ')}.`);
		const dropped = plan.aliases?.dropped || [];
		if (dropped.length) lines.push(`${dropped.map(name => `“${name}”`).join(', ')} ${dropped.length === 1 ? 'doesn’t' : 'don’t'} fit: a tag has at most 10 aliases.`);
		const rewritten = (plan.queries?.rewritten || []).filter(entry => !isCardFilter(entry));
		if (rewritten.length) lines.push(`${formatCount(rewritten.length, privateTags ? 'filter of yours' : 'filter or rule', privateTags ? 'filters of yours' : 'filters and rules')} will name “${intoName}” instead.`);
		return lines;
	};

	// A card's or an element set's own filters the merge rewrites (the person
	// may edit it), listed by card or set so they can look after.
	const cardFilters = (plan?.queries?.rewritten || []).filter(isCardFilter);
	const notFollowing = plan?.queries?.notRewritten || [];
	const leftBehind = result?.queries?.notRewritten || [];

	return (
		<Modal
			open={open}
			title={`Merge “${tag.name}” into…`}
			onCancel={onClose}
			destroyOnHidden
			className="tag-merge-modal"
			footer={result ? [
				<Button key="done" type="primary" onClick={onClose}>Done</Button>,
			] : plan ? [
				<Button key="back" onClick={() => { setPlan(null); setRefusal(null); }} disabled={busy}>Back</Button>,
				<Button key="merge" danger type="primary" loading={busy} onClick={submit} data-testid="tag-merge-confirm">
					{`Merge into “${intoName}”`}
				</Button>,
			] : [
				<Button key="cancel" onClick={onClose}>Cancel</Button>,
				<Button key="next" type="primary" disabled={!choice} loading={checking} onClick={check}>Next</Button>,
			]}
		>
			{refusal ? <Alert type="error" showIcon className="tag-manager__refusal" message={refusal} data-testid="tag-merge-refusal" /> : null}
			{result ? (
				<div className="tag-merge__confirm" data-testid="tag-merge-result">
					<Alert
						type="warning"
						showIcon
						className="tag-merge__warning"
						message={`Merged “${tag.name}” into “${result.into?.name || intoName}”. ${formatCount(leftBehind.length, 'setting', 'settings')} still ${leftBehind.length === 1 ? 'names' : 'name'} “${tag.name}” and ${leftBehind.length === 1 ? 'stops' : 'stop'} matching. Edit ${leftBehind.length === 1 ? 'it' : 'them'} to name “${result.into?.name || intoName}”:`}
						description={<NotFollowingList entries={leftBehind} intoName={result.into?.name || intoName} />}
					/>
				</div>
			) : plan ? (
				<div className="tag-merge__confirm" data-testid="tag-merge-summary">
					<p className="tag-merge__lead">
						<TagBadge tag={tag} size="sm" />
						<span className="tag-merge__arrow" aria-hidden="true">→</span>
						{into ? <TagBadge tag={into} size="sm" /> : null}
					</p>
					<ul className="tag-merge__facts">
						{summary().map(line => <li key={line}>{line}</li>)}
						{cardFilters.length ? (
							<li data-testid="tag-merge-card-filters">
								{`${cardFiltersWords(cardFilters)} you can edit will name “${intoName}” instead:`}
								<ul className="tag-merge__holders">
									{cardFilters.map(entry => (
										<li key={`${entry.holder}:${entry.id}`}>
											<HolderName entry={entry} />
											{` — ${referenceHolderLabel(entry)}`}
										</li>
									))}
								</ul>
							</li>
						) : null}
					</ul>
					{notFollowing.length ? (
						<Alert
							type="warning"
							showIcon
							className="tag-merge__warning"
							message={`${formatCount(notFollowing.length, 'setting', 'settings')} will still name “${tag.name}” and stop matching. ${notFollowing.every(entry => entry.reason === 'card') ? `Someone who can edit ${notFollowing.length === 1 ? 'the card' : 'those cards'} can change ${notFollowing.length === 1 ? 'it' : 'them'} after the merge:` : `Edit ${notFollowing.length === 1 ? 'it' : 'them'} after the merge:`}`}
							description={<NotFollowingList entries={notFollowing} intoName={intoName} />}
						/>
					) : null}
					<p className="tag-merge__final">{`“${tag.name}” is then gone. This can’t be undone.`}</p>
				</div>
			) : (
				<>
					<p className="meta-line">
						{privateTags
							? 'Everything it’s on, the tags under it and its name go to the tag you pick.'
							: 'Everything it’s on, the tags under it, its name and the filters that use it — a card’s own filters too, where you can edit the card — go to the tag you pick.'}
					</p>
					{checking ? <Spin className="tag-merge__checking" /> : null}
					<ul className="tag-move__list" role="listbox" aria-label="Merge it into">
						{targets.map(target => {
							const selected = choice === target.id;
							return (
								<li key={target.id}>
									<button
										type="button"
										role="option"
										aria-selected={selected}
										aria-disabled={target.disabled || undefined}
										disabled={target.disabled}
										className={`tag-move__option${selected ? ' is-selected' : ''}`}
										style={{ '--tag-depth': target.depth }}
										title={target.reason || undefined}
										onClick={() => setChoice(target.id)}
										data-testid={`tag-merge-${target.id}`}
									>
										<TagBadge tag={target.tag} size="xs" />
										{target.reason ? <span className="tag-move__reason">{target.reason}</span> : null}
									</button>
								</li>
							);
						})}
					</ul>
					{!targets.length ? <p className="tag-manager__empty">There is no other tag to merge it into.</p> : null}
				</>
			)}
		</Modal>
	);
}
